/**
 * Browser-tab marks outside production (#1099): a "[DEV] " title prefix and
 * an orange favicon, so a DEV tab can't pass for PROD among other tabs.
 *
 * The prefix survives title changes: an observer on <head> puts it back
 * whenever something (a page setting its own title, a library) replaces the
 * title, keeping that new title after the prefix.
 */
export const DEV_FAVICON_HREF = "/favicon-dev.svg";

export function applyEnvironmentDocumentMarks(doc: Document, label: string): () => void {
  const prefix = `[${label}] `;
  const ensurePrefix = () => {
    if (!doc.title.startsWith(prefix)) {
      doc.title = `${prefix}${doc.title}`;
    }
  };
  ensurePrefix();

  // Replace the icon links rather than editing their href: some browsers only
  // pick up a new favicon from a newly inserted <link>.
  const replaced = [...doc.querySelectorAll<HTMLLinkElement>('link[rel~="icon"]')].map(original => {
    const devIcon = doc.createElement("link");
    devIcon.rel = original.rel;
    devIcon.type = "image/svg+xml";
    devIcon.href = DEV_FAVICON_HREF;
    original.replaceWith(devIcon);
    return { original, devIcon };
  });

  const observer = new MutationObserver(ensurePrefix);
  observer.observe(doc.head, { childList: true, subtree: true, characterData: true });

  return () => {
    observer.disconnect();
    if (doc.title.startsWith(prefix)) {
      doc.title = doc.title.slice(prefix.length);
    }
    replaced.forEach(({ original, devIcon }) => devIcon.replaceWith(original));
  };
}
